import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomOrdersSettingsApiV1OrdersSettingsServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/orders-settings',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/orders-settings',
        destPath: '/v1/orders-settings',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/ecom/v1/orders-settings',
        destPath: '/v1/orders-settings',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/ecom/v1/orders-settings',
        destPath: '/v1/orders-settings',
      },
    ],
    _: [
      {
        srcPath: '/ecom/v1/orders-settings',
        destPath: '/v1/orders-settings',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_orders-settings';

/**
 * Retrieves the sites' order settings.
 *
 *
 * The `getOrdersSettings()` function returns a Promise that resolves to orders settings.
 */
export function getOrdersSettings(payload: object): RequestOptionsFactory<any> {
  function __getOrdersSettings({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.orders_settings',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.orders_settings.api.v1.OrdersSettingsService.GetOrdersSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomOrdersSettingsApiV1OrdersSettingsServiceUrl({
        protoPath: '/v1/orders-settings',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ordersSettings.createdDate' },
              { path: 'ordersSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getOrdersSettings;
}

/**
 * Updates the sites' orders settings.
 *
 *
 * The `updateOrdersSettings()` function returns a Promise that resolves to the newly updated orders settings.
 */
export function updateOrdersSettings(
  payload: object
): RequestOptionsFactory<any> {
  function __updateOrdersSettings({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          { path: 'ordersSettings.createdDate' },
          { path: 'ordersSettings.updatedDate' },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.orders_settings',
      method: 'PATCH' as any,
      methodFqn:
        'wix.ecom.orders_settings.api.v1.OrdersSettingsService.UpdateOrdersSettings',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixEcomOrdersSettingsApiV1OrdersSettingsServiceUrl({
        protoPath: '/v1/orders-settings',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              { path: 'ordersSettings.createdDate' },
              { path: 'ordersSettings.updatedDate' },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateOrdersSettings;
}
